import React, { useState, useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import {
  Button,
  Divider,
  FormHelperText,
  Grid,
  Icon,
  LinearProgress,
  TextField,
  Typography,
  Snackbar
} from '@material-ui/core';
import Autocomplete from '@material-ui/lab/Autocomplete';
import MuiAlert from '@material-ui/lab/Alert';
import parse from 'autosuggest-highlight/parse';
import match from 'autosuggest-highlight/match';
import { getAssetManagerImagesApi } from 'api';
import {
  changeHomepageCTABlockConfig,
  changeHomepageCTACategory
} from 'redux/actions/store_configs';
import { ImageGalleryDialog } from 'components';
import { assetManagerUrl } from 'configs';
import { uploadImage, buildTagIds, TAG_CODES } from './utils';


type BlockType = 'vertical' | 'horizontal_top' | 'horizontal_bottom';

interface BlockConfig {
  title: string;
  titleKey: string;
  categoryKey: string;
  desktopImagePath: string;
  mobileImagePath: string;
  desktopInputId: string;
  mobileInputId: string;
  desktopCols: number;
  desktopResolution: string;
  dialogTitlePrefix: string;
}

const BLOCK_CONFIG: Record<BlockType, BlockConfig> = {
  horizontal_top: {
    title: 'CTA Top Left Block',
    titleKey: 'store_info/cta/cta_horizontal_top_title',
    categoryKey: 'store_info/cta/cta_horizontal_top_mask_button_categoryId',
    desktopImagePath: 'dermpro/design/cta_image_2_desktop_lite',
    mobileImagePath: 'dermpro/design/cta_image_2_mobile_lite',
    desktopInputId: 'upload-desktop-image',
    mobileInputId: 'upload-mobile-image',
    desktopCols: 3,
    desktopResolution: '983x491px',
    dialogTitlePrefix: 'Top Left Block',
  },
  horizontal_bottom: {
    title: 'CTA Top Right Block',
    titleKey: 'store_info/cta/cta_horizontal_bottom_title',
    categoryKey: 'store_info/cta/cta_horizontal_bottom_mask_button_categoryId',
    desktopImagePath: 'dermpro/design/cta_image_3_desktop_lite',
    mobileImagePath: 'dermpro/design/cta_image_3_mobile_lite',
    desktopInputId: 'upload-desktop-image-bottom',
    mobileInputId: 'upload-mobile-image-bottom',
    desktopCols: 3,
    desktopResolution: '983x491px',
    dialogTitlePrefix: 'Top Right Block',
  },
  vertical: {
    title: 'CTA Bottom Block',
    titleKey: 'store_info/cta/cta_vertical_title',
    categoryKey: 'store_info/cta/cta_vertical_mask_button_categoryId',
    desktopImagePath: 'dermpro/design/cta_image_1_desktop_lite',
    mobileImagePath: 'dermpro/design/cta_image_1_mobile_lite',
    desktopInputId: 'upload-desktop-image',
    mobileInputId: 'upload-mobile-image',
    desktopCols: 2,
    desktopResolution: '2025x491px',
    dialogTitlePrefix: 'Bottom Block',
  },
};

const CTABlockForm: React.FC<CTABlockFormProps> = ({ blockType, values, setFieldValue }) => {
  const dispatch = useDispatch();
  const config = BLOCK_CONFIG[blockType];
  const blockValues = values[blockType];

  const [openImageDialog, toggleOpenImageDialog] = useState<boolean>(false);
  const [selectedImageValues, setSelectedImageValues] = useState<SelectedImageValues | null>(null);
  const [openOwnDesktopImageDialog, toggleOpenOwnDesktopImageDialog] = useState<boolean>(false);
  const [openOwnMobileImageDialog, toggleOpenOwnMobileImageDialog] = useState<boolean>(false);
  const [ownDesktopImagesTagIds, setOwnDesktopImagesTagIds] = useState<string>('');
  const [ownMobileImagesTagIds, setOwnMobileImagesTagIds] = useState<string>('');

  const {
    storeActiveCategoriesWithActiveNavLevel,
    loadingCategories,
    errorCategories
  } = useSelector((state: IReduxState) => state.categories);

  const {
    assetCategoryTags,
    assetImagesData,
    currentCategoryId
  } = useSelector((state: IReduxState) => state.asset_manager);

  const { profile: { stores = [] } } = useSelector((state: IReduxState) => state.user);

  const storeCode = stores.length && stores[0]?.storeCode;

  const desktopTagData = assetCategoryTags.find(
    (tag) => tag.tagCode === TAG_CODES[blockType].desktop
  );
  const mobileTagData = assetCategoryTags.find(
    (tag) => tag.tagCode === TAG_CODES[blockType].mobile
  );
  const privateTagData = assetCategoryTags.find((tag) => tag.tagCode === 'private');
  const storeCodeTagData = assetCategoryTags.find((tag) => tag.tagCode === `${storeCode}`);

  useEffect(() => {
    const desktopTagIds = buildTagIds(desktopTagData, privateTagData, storeCodeTagData);
    const mobileTagIds = buildTagIds(mobileTagData, privateTagData, storeCodeTagData);

    setOwnDesktopImagesTagIds(desktopTagIds);
    setOwnMobileImagesTagIds(mobileTagIds);
  }, [desktopTagData, mobileTagData, privateTagData, storeCodeTagData]);

  useEffect(() => {
    if (ownDesktopImagesTagIds) {
      getAssetManagerImagesApi({
        dispatch,
        limit: 18,
        page: 1,
        tagIds: ownDesktopImagesTagIds,
        isPrivate: true
      });
    }

    if (ownMobileImagesTagIds) {
      getAssetManagerImagesApi({
        dispatch,
        limit: 20,
        page: 1,
        tagIds: ownMobileImagesTagIds,
        isPrivate: true
      });
    }
  }, [dispatch, ownDesktopImagesTagIds, ownMobileImagesTagIds]);

  const handleChange = (event: React.ChangeEvent<HTMLInputElement>): void => {
    const { name, checked, type, value } = event.target;
    dispatch(changeHomepageCTABlockConfig(name, checked, type, value, blockType));
    setFieldValue(`${blockType}[${name}]`, value);
  };

  const handleSelectCategory = (
    value: ICategory | null,
    actionType: string,
    configTab: string,
    configPath: string
  ): void => {
    dispatch(changeHomepageCTACategory(actionType, value, configTab, configPath));
    setFieldValue(`${blockType}[${configPath}]`, value ? value.id : '');
  };

  const handleSelectImage = (data: AssetImage, path: string): void => {
    const value = `${assetManagerUrl}/${data?.webPath}`;
    dispatch(changeHomepageCTABlockConfig(path, null, 'text', value, blockType));
    setFieldValue(`${blockType}[${path}]`, value);
  };

  const handleUpLoadImage = async (
    event: React.ChangeEvent<HTMLInputElement>,
    imageType: ImageType,
    path: string
  ): Promise<void> => {
    const { files, value } = event.target;

    if (!files || !storeCode) {
      return;
    }

    const uploadedData = await uploadImage(
      {
        dispatch,
        files,
        value,
        imageType,
        ownImagesTagIds: imageType === 'Desktop' ? ownDesktopImagesTagIds : ownMobileImagesTagIds,
        storeCode,
        desktopTagData,
        mobileTagData,
        privateTagData,
        currentCategoryId
      },
      blockType
    );

    if (uploadedData) {
      handleSelectImage(uploadedData, path);
    }
  };

  const openImageLibrary = (type: ImageType, path: string, tagId: string | number): void => {
    toggleOpenImageDialog(true);
    setSelectedImageValues({
      tagId,
      path,
      cols: type === 'Desktop' ? config.desktopCols : 4,
      rowHeight: type === 'Desktop' ? 160 : 340,
      type
    });
  };

  const openOwnImages = (type: ImageType, path: string, tagIds: string): void => {
    const toggleDialog = type === 'Desktop'
      ? toggleOpenOwnDesktopImageDialog
      : toggleOpenOwnMobileImageDialog;

    toggleDialog(true);
    setSelectedImageValues({
      tagId: tagIds,
      path,
      cols: type === 'Desktop' ? config.desktopCols : 4,
      rowHeight: type === 'Desktop' ? 160 : 340,
      type
    });
  };

  return (
    <div>
      <Typography variant="h4" className="my-6 mx-3">{config.title}</Typography>
      <Divider className="mb-8" />

      <Grid container spacing={3} className="mb-4">
        <Grid item xs={12} sm={2} className="my-2 ml-4">Block Title</Grid>
        <Grid item xs={12} sm={9}>
          <TextField
            fullWidth
            variant="outlined"
            name={config.titleKey}
            label="Text"
            value={blockValues[config.titleKey]}
            onChange={handleChange}
            InputLabelProps={{ shrink: true }}
          />
        </Grid>

        <Grid item xs={12} sm={2} className="my-2 ml-4">Block Button Category</Grid>
        <Grid item xs={12} sm={9}>
          {loadingCategories && <LinearProgress className="mt-4" />}
          {!loadingCategories && errorCategories && (
            <div>Can&apos;t fetch categories data</div>
          )}
          {!loadingCategories && !errorCategories && storeActiveCategoriesWithActiveNavLevel.length && (
            <Autocomplete<ICategory>
              size="small"
              fullWidth
              options={storeActiveCategoriesWithActiveNavLevel}
              getOptionLabel={(option) => option.nameWithPath || ''}
              value={
                storeActiveCategoriesWithActiveNavLevel.find(
                  (category) => category.id == blockValues[config.categoryKey]
                ) || null
              }
              getOptionSelected={(option) => blockValues[config.categoryKey] == option.id}
              onChange={(_, value, action) =>
                handleSelectCategory(
                  value as ICategory | null,
                  action,
                  blockType,
                  config.categoryKey
                )
              }
              renderInput={(params) => (
                <TextField {...params} variant="outlined" label="Category" />
              )}
              renderOption={(option, { inputValue }) => {
                const matches = match(option.nameWithPath, inputValue);
                const parts = parse(option.nameWithPath, matches);
                return (
                  <div>
                    {parts.map((part, index) => (
                      <span
                        key={index}
                        className={part.highlight ? 'text-primary font-bold' : ''}
                      >
                        {part.text}
                      </span>
                    ))}
                  </div>
                );
              }}
            />
          )}
        </Grid>

        {/* Desktop Image Upload */}
        <Grid item xs={12} sm={2} className="my-2 ml-4">Block Image Desktop</Grid>
        <Grid item xs={12} sm={9}>
          <div>
            <Button
              className="w-full max-w-300"
              variant="outlined"
              color="primary"
              size="large"
            >
              <label htmlFor={config.desktopInputId} className="flex items-center">
                <Icon className="mr-2">upload</Icon>
                Upload Own Desktop Image
              </label>
            </Button>
            <input
              className="hidden"
              onChange={(event) => handleUpLoadImage(event, 'Desktop', config.desktopImagePath)}
              onClick={(event) => (event.currentTarget.value = '')}
              id={config.desktopInputId}
              type="file"
              accept="image/png, image/jpeg"
            />
          </div>
          <FormHelperText className="mt-2">
            <div>Files supported: png, jpeg, jpg. File size should be less than 250kB.</div>
            <div>Image resolution should be exactly {config.desktopResolution}.</div>
          </FormHelperText>

          <Button
            className="w-full max-w-300 mt-2 mr-2"
            variant="outlined"
            color="primary"
            size="large"
            onClick={() =>
              desktopTagData &&
              openImageLibrary('Desktop', config.desktopImagePath, desktopTagData.id)
            }
          >
            <Icon className="mr-2">wallpaper</Icon>
            Select Image From Library
          </Button>

          <Button
            className="w-full max-w-300 mt-2 mr-2 bg-periwinkle-gray"
            variant="outlined"
            color="primary"
            size="large"
            onClick={() => openOwnImages('Desktop', config.desktopImagePath, ownDesktopImagesTagIds)}
          >
            <Icon className="mr-2">wallpaper</Icon>
            Select From Own Images
          </Button>
        </Grid>

        {/* Mobile Image Upload */}
        <Grid item xs={12} sm={2} className="my-2 ml-4">Block Image Mobile</Grid>
        <Grid item xs={12} sm={9}>
          <div>
            <Button
              className="w-full max-w-300"
              variant="outlined"
              color="primary"
              size="large"
            >
              <label htmlFor={config.mobileInputId} className="flex items-center">
                <Icon className="mr-2">upload</Icon>
                Upload Own Mobile Image
              </label>
            </Button>
            <input
              className="hidden"
              onChange={(event) => handleUpLoadImage(event, 'Mobile', config.mobileImagePath)}
              onClick={(event) => (event.currentTarget.value = '')}
              id={config.mobileInputId}
              type="file"
              accept="image/png, image/jpeg"
            />
          </div>
          <FormHelperText className="mt-2">
            <div>Files supported: png, jpeg, jpg. File size should be less than 250kB.</div>
            <div>Image resolution should be exactly 380x552px.</div>
          </FormHelperText>

          <Button
            className="w-full max-w-300 mt-2 mr-2"
            variant="outlined"
            color="primary"
            size="large"
            onClick={() =>
              mobileTagData &&
              openImageLibrary('Mobile', config.mobileImagePath, mobileTagData.id)
            }
          >
            <Icon className="mr-2">wallpaper</Icon>
            Select Image From Library
          </Button>

          <Button
            className="w-full max-w-300 mt-2 mr-2 bg-periwinkle-gray"
            variant="outlined"
            color="primary"
            size="large"
            onClick={() => openOwnImages('Mobile', config.mobileImagePath, ownMobileImagesTagIds)}
          >
            <Icon className="mr-2">wallpaper</Icon>
            Select From Own Images
          </Button>
        </Grid>
      </Grid>

      <Divider className="my-8" />

      {/* Image Gallery Dialogs */}
      {openImageDialog && selectedImageValues?.tagId && (
        <ImageGalleryDialog
          title={`Select ${config.dialogTitlePrefix} CTA ${selectedImageValues.type} Image From Library`}
          open={openImageDialog}
          setOpen={toggleOpenImageDialog}
          onImageSelect={(data: AssetImage) =>
            handleSelectImage(data, selectedImageValues.path)
          }
          imageTagId={selectedImageValues.tagId}
          cols={selectedImageValues.cols}
          rowHeight={selectedImageValues.rowHeight}
        />
      )}

      {openOwnDesktopImageDialog &&
        ownDesktopImagesTagIds &&
        assetImagesData[ownDesktopImagesTagIds]?.items?.length ? (
          <ImageGalleryDialog
            title="Select Desktop CTA Image From Own Images"
            open={openOwnDesktopImageDialog}
            setOpen={toggleOpenOwnDesktopImageDialog}
            onImageSelect={(data: AssetImage) =>
              selectedImageValues && handleSelectImage(data, selectedImageValues.path)
            }
            imageTagId={ownDesktopImagesTagIds}
            isPrivate
            cols={config.desktopCols}
            rowHeight={160}
          />
        ) : (
          <Snackbar
            anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
            autoHideDuration={3000}
            onClose={() => toggleOpenOwnDesktopImageDialog(false)}
            open={openOwnDesktopImageDialog}
          >
            <MuiAlert elevation={6} variant="filled" severity="warning">
              You have no your own uploaded images. Firstly, you have to upload at least one image.
            </MuiAlert>
          </Snackbar>
        )}

      {openOwnMobileImageDialog &&
        ownMobileImagesTagIds &&
        assetImagesData[ownMobileImagesTagIds]?.items?.length ? (
          <ImageGalleryDialog
            title="Select Mobile CTA Image From Own Images"
            open={openOwnMobileImageDialog}
            setOpen={toggleOpenOwnMobileImageDialog}
            onImageSelect={(data: AssetImage) =>
              selectedImageValues && handleSelectImage(data, selectedImageValues.path)
            }
            imageTagId={ownMobileImagesTagIds}
            isPrivate
            cols={4}
            rowHeight={340}
          />
        ) : (
          <Snackbar
            anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
            autoHideDuration={3000}
            onClose={() => toggleOpenOwnMobileImageDialog(false)}
            open={openOwnMobileImageDialog}
          >
            <MuiAlert elevation={6} variant="filled" severity="warning">
              You have no your own uploaded images. Firstly, you have to upload at least one image.
            </MuiAlert>
          </Snackbar>
        )}
    </div>
  );
};

export default CTABlockForm;
